<script setup>
import { computed, ref } from "vue"

const props = defineProps({
  endpoint: { type: String, required: true },
  fields: { type: Array, required: true },
  contextLabel: { type: String, default: "表單" },
})

const open = ref(false)
const loading = ref(false)
const error = ref("")
const transcript = ref("")
const result = ref(null)
const sourceSnapshot = ref({})
const appliedValues = ref({})

const suggestions = computed(() => result.value?.output?.suggestions ?? [])
const unresolved = computed(() => result.value?.output?.unresolved ?? [])
const riskySuggestions = computed(() => suggestions.value.filter((item) => (
  item.review_required || item.confidence === "low"
)))

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ""
}

function inputFor(field) {
  const definition = props.fields.find((item) => item.key === field)
  return definition ? document.querySelector(definition.selector) : null
}

function currentFields() {
  return Object.fromEntries(props.fields.map((field) => [
    field.key,
    inputFor(field.key)?.value ?? "",
  ]))
}

function sourceChanged() {
  const current = currentFields()
  return props.fields.some(({ key }) => {
    const expected = appliedValues.value[key] ?? sourceSnapshot.value[key] ?? ""
    return current[key] !== expected
  })
}

function showDrawer() {
  open.value = true
  error.value = ""
}

function closeDrawer() {
  open.value = false
}

async function requestSuggestions() {
  if (!transcript.value.trim()) {
    error.value = "請先貼上會議逐字稿。"
    return
  }

  loading.value = true
  error.value = ""
  result.value = null
  appliedValues.value = {}
  sourceSnapshot.value = currentFields()
  try {
    const response = await fetch(props.endpoint, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify({
        transcript: transcript.value,
        current_fields: sourceSnapshot.value,
      }),
    })
    const payload = await response.json()
    if (!response.ok) throw new Error(payload?.error?.message || `AI 請求失敗（${response.status}）`)
    result.value = payload
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "AI 助手暫時無法提供建議。"
  } finally {
    loading.value = false
  }
}

function applyValue(suggestion) {
  const input = inputFor(suggestion.field)
  if (!input) return false

  input.value = suggestion.value
  input.dispatchEvent(new Event("input", { bubbles: true }))
  input.dispatchEvent(new Event("change", { bubbles: true }))
  appliedValues.value = { ...appliedValues.value, [suggestion.field]: suggestion.value }
  return true
}

function ensureFresh() {
  if (!sourceChanged()) return true
  error.value = "表單內容已在 AI 處理後變更。為避免覆蓋新內容，請重新產生建議。"
  return false
}

function applySuggestion(suggestion) {
  if (!ensureFresh()) return
  applyValue(suggestion)
}

function applyAll() {
  if (!ensureFresh() || !suggestions.value.length) return
  if (riskySuggestions.value.length && !window.confirm(
    `其中 ${riskySuggestions.value.length} 個欄位標示為低信心或待人工確認，仍要全部套用到表單嗎？`,
  )) return

  suggestions.value.forEach(applyValue)
}

function confidenceLabel(value) {
  return { high: "高信心", medium: "中信心", low: "低信心" }[value] || value
}
</script>

<template>
  <div class="ai-transcript-assistant">
    <button type="button" class="ai-sprite-trigger" @click="showDrawer">
      <span class="ai-sprite-trigger__spark" aria-hidden="true">✦</span>
      <span><small>AI 小精靈</small>從逐字稿填寫</span>
    </button>

    <Teleport to="body">
      <div v-if="open" class="ai-review-backdrop" @click.self="closeDrawer">
        <aside class="ai-review-drawer" role="dialog" aria-modal="true" aria-labelledby="ai-review-title">
          <header class="ai-review-drawer__header">
            <div>
              <p>Transcript desk · {{ contextLabel }}</p>
              <h2 id="ai-review-title">逐字稿整理精靈</h2>
            </div>
            <button type="button" aria-label="關閉 AI 建議" @click="closeDrawer">×</button>
          </header>

          <section class="ai-transcript-source" aria-labelledby="ai-transcript-source-title">
            <div class="ai-review-section-heading">
              <span>01</span>
              <div>
                <h3 id="ai-transcript-source-title">貼上會議逐字稿</h3>
                <p>內容只用於這次欄位整理；AI 不會直接儲存或送出表單。</p>
              </div>
            </div>
            <textarea
              v-model="transcript"
              rows="10"
              maxlength="100000"
              placeholder="將錄音轉出的逐字稿貼在這裡……"
              :disabled="loading"
            ></textarea>
            <div class="ai-transcript-source__footer">
              <span>{{ transcript.length.toLocaleString() }}／100,000 字</span>
              <button type="button" :disabled="loading || !transcript.trim()" @click="requestSuggestions">
                {{ loading ? "正在整理脈絡…" : "產生欄位建議" }}
              </button>
            </div>
          </section>

          <div v-if="loading" class="ai-thinking" role="status">
            <span aria-hidden="true"></span>
            <div><strong>正在閱讀逐字稿</strong><p>比對欄位、保留原意並找出可核對的原句。</p></div>
          </div>
          <p v-if="error" class="ai-review-error" role="alert">{{ error }}</p>

          <section v-if="result" class="ai-suggestion-results" aria-labelledby="ai-suggestion-results-title">
            <div class="ai-review-section-heading">
              <span>02</span>
              <div>
                <h3 id="ai-suggestion-results-title">檢視並套用建議</h3>
                <p>{{ result.output.summary || "逐字稿整理完成。" }}</p>
              </div>
            </div>

            <div v-if="suggestions.length" class="ai-suggestion-list">
              <article
                v-for="suggestion in suggestions"
                :key="suggestion.field"
                class="ai-suggestion-card"
                :class="{ 'is-applied': appliedValues[suggestion.field] !== undefined }"
              >
                <header>
                  <div>
                    <span class="ai-confidence" :class="`is-${suggestion.confidence}`">
                      {{ confidenceLabel(suggestion.confidence) }}
                    </span>
                    <h4>{{ suggestion.label }}</h4>
                  </div>
                  <button type="button" @click="applySuggestion(suggestion)">
                    {{ appliedValues[suggestion.field] !== undefined ? "已套用" : "套用此欄" }}
                  </button>
                </header>
                <div class="ai-suggestion-comparison">
                  <div><small>目前內容</small><pre>{{ sourceSnapshot[suggestion.field] || "（空白）" }}</pre></div>
                  <div><small>建議內容</small><pre>{{ suggestion.value }}</pre></div>
                </div>
                <details>
                  <summary>查看逐字稿依據</summary>
                  <blockquote v-for="(quote, index) in suggestion.evidence" :key="index">{{ quote }}</blockquote>
                </details>
                <p v-if="suggestion.review_required" class="ai-review-warning">
                  <strong>請人工確認</strong>{{ suggestion.review_reason }}
                </p>
              </article>
            </div>
            <p v-else class="ai-no-suggestions">逐字稿中沒有足以安全填入欄位的明確資訊。</p>

            <details v-if="unresolved.length" class="ai-unresolved">
              <summary>{{ unresolved.length }} 項待確認資訊</summary>
              <ul><li v-for="item in unresolved" :key="item">{{ item }}</li></ul>
            </details>
          </section>

          <footer class="ai-review-drawer__footer">
            <div>
              <button type="button" class="is-quiet" @click="closeDrawer">保留目前內容</button>
              <button type="button" :disabled="!suggestions.length" @click="applyAll">套用全部建議</button>
            </div>
          </footer>
        </aside>
      </div>
    </Teleport>
  </div>
</template>
